import { CheckboxOption } from "@/components/ui/checkbox"
import { RadioOption } from "@/components/ui/radio"

/**
 * Which District Councils (IUPAT) or Locals (a District Council) a setting
 * covers.
 *
 * "All of X" is deliberately not the same as ticking every box: it is evaluated
 * fresh on every run, so a Local added to the District Council next month is
 * included without anyone revisiting this screen. For IUPAT it means the whole
 * union, orphaned Locals included.
 */
export function TargetPicker({
  targets,
  targetNoun,
  coversAll,
  selectedIds,
  organizationName,
  coversWholeRoster,
  error,
  onCoversAllChange,
  onSelectedIdsChange,
}: {
  targets: { id: number; name: string }[]
  /** "Local" or "District Council" — what this organization picks from. */
  targetNoun: string
  coversAll: boolean
  selectedIds: number[]
  organizationName: string
  /** True for IUPAT, where covering everything means the entire union. */
  coversWholeRoster: boolean
  error?: string
  onCoversAllChange: (value: boolean) => void
  onSelectedIdsChange: (ids: number[]) => void
}) {
  const plural = `${targetNoun}s`
  const toggle = (id: number, checked: boolean) => {
    onSelectedIdsChange(
      checked ? [...selectedIds, id] : selectedIds.filter((each) => each !== id),
    )
  }

  const allSelected = targets.length > 0 && selectedIds.length === targets.length

  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="Who this reaches" className="space-y-2">
        <RadioOption
          name="covers_all_locals"
          checked={coversAll}
          onChange={() => onCoversAllChange(true)}
          label={`All of ${organizationName}`}
          description={
            coversWholeRoster
              ? `Every member of the union — all ${targets.length} District Councils and every Local, including those under no District Council.`
              : `${targets.length} ${targets.length === 1 ? targetNoun : plural} today, plus any added later.`
          }
        />

        <RadioOption
          name="covers_all_locals"
          checked={!coversAll}
          onChange={() => onCoversAllChange(false)}
          label={`Specific ${plural}`}
          description={`A fixed list. ${plural} added later are not included until you add them here.`}
        />
      </div>

      {!coversAll && (
        <div className="rounded-md border border-hairline bg-page">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-3 py-2">
            <span className="text-xs text-ink-muted">
              {selectedIds.length} of {targets.length} selected
            </span>
            <button
              type="button"
              className="toggle-button"
              onClick={() =>
                onSelectedIdsChange(allSelected ? [] : targets.map((t) => t.id))
              }
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          </div>

          {targets.length === 0 ? (
            <p className="px-3 py-4 text-xs text-ink-muted">
              {organizationName} has no {plural} to pick from.
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto p-3">
              <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                {targets.map((target) => (
                  <CheckboxOption
                    key={target.id}
                    checked={selectedIds.includes(target.id)}
                    onChange={(event) => toggle(target.id, event.target.checked)}
                    label={target.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-xs text-danger-display">{error}</p>}
    </div>
  )
}
